"use client";
import React, { useEffect, useRef, useState } from "react";
import { Check } from "lucide-react";
import { ICON_SIZE } from "modules/common/components/navigation/Menu";
import { UpdateUserStoresRequest } from "modules/lgs/model/lgs";
import { useController, useForm } from "react-hook-form";
import { useAuth } from "@clerk/nextjs";
import { updateUserStores } from "modules/server/actions/lgs";
import { ShortLGS } from "modules/common/model/models";
import { QUERIES } from "modules/common/query";
import { useQueryClient } from "@tanstack/react-query";
import { useServerAction } from "modules/common/components/toast/useServerAction";
import { usePathname, useRouter } from "next/navigation";
import {
  useLGSSettingsDialog,
  useSelectedStores,
} from "modules/lgs/stores/useLGSSettingsStore";
import StoreSelectorGrid from "modules/lgs/components/StoreSelectorGrid";
import DynamicFindStoresMap from "modules/common/components/map/DynamicFindStoresMap";
import { cn } from "utils/utils";
import { getUserFriendlyMessage } from "utils/error-sanitizer";

interface SelectLGSsFormProps {
  userLgs: ShortLGS[];
  padding?: string;
  /** Store map filling the right column on wide containers. Off where the page brings its own map. */
  showMap?: boolean;
  /** Use the denser settings-page treatment for the store lists. */
  compact?: boolean;
  /** Settings autosave by default; onboarding commits its whole draft once. */
  autosave?: boolean;
  /** Rendered above the lists in the left column, e.g. the city search field */
  children?: React.ReactNode;
}

interface StoreSelectionValues {
  lgs: number[];
}

const AUTOSAVE_DELAY_MS = 600;
// How long the "Saved" checkmark stays visible after a successful save
const SAVED_CONFIRMATION_MS = 2000;
// Mirrors the API constraint (user-service: 1–5 stores per user)
const MAX_STORES = 5;

type SaveState = "idle" | "saving" | "saved";

function idsKey(stores: { wotcId: number }[]): string {
  return stores
    .map((store) => store.wotcId)
    .sort((a, b) => a - b)
    .join(",");
}

function validateCount(count: number): string | null {
  if (count === 0) return "Select at least one trading location";
  if (count > MAX_STORES)
    return `You can select up to ${MAX_STORES} trading locations.`;
  return null;
}

export function SelectLGSsForm({
  userLgs,
  padding,
  showMap = true,
  compact = false,
  autosave = true,
  children,
}: SelectLGSsFormProps) {
  const auth = useAuth();
  const userId = auth.userId as string;
  const queryClient = useQueryClient();
  const router = useRouter();
  const pathname = usePathname();
  const { setOpen } = useLGSSettingsDialog();
  const { selectedStores, setSelectedStores } = useSelectedStores();

  // Drives the spinner/checkmark indicator in the "Your stores" list header
  const [saveState, setSaveState] = useState<SaveState>("idle");

  // react-hook-form owns validation for the virtual store-selection field
  const { control } = useForm<StoreSelectionValues>({
    mode: "onChange",
    defaultValues: { lgs: userLgs.map((store) => store.wotcId) },
  });
  const { field, fieldState } = useController({
    control,
    name: "lgs",
    rules: { validate: (ids) => validateCount(ids.length) ?? true },
  });

  // Callers mount the form only after userLgs has settled. Capture that first
  // snapshot — including an empty one — so an optimistic query-cache echo can
  // never be mistaken for a server-confirmed save baseline.
  const initialStores = useRef<ShortLGS[]>(userLgs);
  const savedStores = useRef<ShortLGS[]>(initialStores.current);

  // Initialize Zustand once from the loaded snapshot. Later userLgs updates can
  // be our own optimistic cache write, so they must not reset savedStores.
  useEffect(() => {
    // The onboarding wrapper owns its draft across visual step unmounts.
    if (!autosave) return;
    setSelectedStores(initialStores.current);
  }, [autosave, setSelectedStores]);

  // Successful saves stay silent; failures remain visible inline.
  const {
    execute: submitUpdateStores,
    isPending,
    error: saveError,
  } = useServerAction(updateUserStores, {
    showSuccessToast: false,
    showErrorToast: false,
  });

  // Save the given selection with optimistic cache update + rollback.
  // The autosave queue below guarantees this is never called concurrently.
  async function saveStores(stores: ShortLGS[]) {
    const previousSaved = savedStores.current;
    if (
      validateCount(stores.length) ||
      idsKey(stores) === idsKey(previousSaved)
    ) {
      return;
    }

    // Union of previously saved + new stores (deduplicated) for cache invalidation
    const affectedStores = new Map<number, { wotcId: number; slug: string }>();
    for (const store of [...previousSaved, ...stores]) {
      affectedStores.set(store.wotcId, {
        wotcId: store.wotcId,
        slug: store.slug,
      });
    }

    const req: UpdateUserStoresRequest = {
      userId,
      lgs: stores.map((store) => store.wotcId),
      affectedStores: Array.from(affectedStores.values()),
    };

    // Re-assert the optimistic selection when a queued save starts. On failure,
    // roll back to the last server-confirmed snapshot rather than this value.
    queryClient.setQueryData(
      [QUERIES.LGS_USER, userId],
      stores.map((store) => ({ ...store }))
    );

    const result = await submitUpdateStores(req);
    if (!result.success) {
      queryClient.setQueryData(
        [QUERIES.LGS_USER, userId],
        previousSaved.map((store) => ({ ...store }))
      );
      setSaveState("idle");
      return;
    }

    savedStores.current = stores;
    setSaveState("saved");
    setTimeout(
      () => setSaveState((state) => (state === "saved" ? "idle" : state)),
      SAVED_CONFIRMATION_MS
    );

    // If the user removed the store whose page they're on, redirect to their first store
    const storePageMatch = pathname.match(/^\/x\/stores\/(.+)$/);
    if (storePageMatch) {
      const currentStoreSlug = storePageMatch[1];
      const isCurrentStoreStillSelected = stores.some(
        (store) => store.slug === currentStoreSlug
      );
      if (!isCurrentStoreStillSelected && stores.length > 0) {
        setOpen(false);
        router.push(`/x/stores/${stores[0].slug}`);
      }
    }

    // Refetch in the background without a refresh
    queryClient.invalidateQueries({
      queryKey: [QUERIES.LGS_USER, userId],
      refetchType: "none",
    });

    // Invalidate city & LGS client queries so they refetch fresh data
    queryClient.invalidateQueries({ queryKey: [QUERIES.CITY_CARDS] });
    queryClient.invalidateQueries({ queryKey: [QUERIES.CITY_STATS] });
    queryClient.invalidateQueries({ queryKey: [QUERIES.CITY_MATCHED_CARDS] });
    queryClient.invalidateQueries({ queryKey: [QUERIES.CITY_CARDS_PREVIEW] });
    queryClient.invalidateQueries({ queryKey: [QUERIES.LGS_CARDS] });
    queryClient.invalidateQueries({ queryKey: [QUERIES.LGS_MATCHED_CARDS] });
    queryClient.invalidateQueries({
      queryKey: [QUERIES.TRADE_PROPOSAL_LOCATION_OPTIONS],
    });
  }

  const latestStores = useRef(selectedStores);
  const debounceTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const saveIsReady = useRef(false);
  const saveInFlight = useRef(false);
  const saveStoresRef = useRef(saveStores);
  saveStoresRef.current = saveStores;

  // Run one save at a time. If the selection changes during a request, the
  // debounce marks the latest snapshot as ready and this drains it afterward.
  // Any intermediate snapshots are discarded.
  function drainSaveQueue() {
    if (saveInFlight.current || !saveIsReady.current) return;

    saveIsReady.current = false;
    const stores = latestStores.current;
    if (
      validateCount(stores.length) ||
      idsKey(stores) === idsKey(savedStores.current)
    ) {
      setSaveState("idle");
      return;
    }

    saveInFlight.current = true;
    void saveStoresRef.current(stores).finally(() => {
      saveInFlight.current = false;
      if (saveIsReady.current) drainSaveQueue();
    });
  }

  // Auto-save (debounced) whenever the user changes the selection
  const isFirstRun = useRef(true);
  useEffect(() => {
    latestStores.current = selectedStores;

    // Skip the mount run — only user-driven changes should validate or save
    if (isFirstRun.current) {
      isFirstRun.current = false;
      return;
    }

    field.onChange(selectedStores.map((store) => store.wotcId));

    // Onboarding owns persistence for its complete stores + preferences draft.
    // Keeping this form selector-only there avoids launching a Server Action
    // from unmount cleanup ahead of the required-setup request.
    if (!autosave) {
      setSaveState("idle");
      return;
    }

    if (debounceTimer.current) clearTimeout(debounceTimer.current);
    saveIsReady.current = false;
    if (validateCount(selectedStores.length)) {
      setSaveState("idle");
      return;
    }

    // The selection is the optimistic source of truth. Update consumers now;
    // persistence remains debounced and rolls this cache back on failure.
    queryClient.setQueryData(
      [QUERIES.LGS_USER, userId],
      selectedStores.map((store) => ({ ...store }))
    );
    setSaveState(
      idsKey(selectedStores) === idsKey(savedStores.current) ? "idle" : "saving"
    );

    const timer = setTimeout(() => {
      debounceTimer.current = null;
      saveIsReady.current = true;
      drainSaveQueue();
    }, AUTOSAVE_DELAY_MS);
    debounceTimer.current = timer;

    return () => {
      clearTimeout(timer);
      if (debounceTimer.current === timer) debounceTimer.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedStores]);

  // Do not drop a valid final selection when the user closes the dialog or
  // leaves the page before the trailing debounce fires.
  useEffect(() => {
    return () => {
      if (!autosave) return;
      if (debounceTimer.current) clearTimeout(debounceTimer.current);
      saveIsReady.current = true;
      drainSaveQueue();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const saveIndicator = (
    <span aria-live="polite" className="flex items-center">
      {saveState === "saving" && (
        <>
          <span className="loading loading-spinner loading-xs text-base-content/60" />
          <span className="sr-only">Saving your stores</span>
        </>
      )}
      {saveState === "saved" && (
        <span className="text-success flex items-center gap-1 text-xs font-normal">
          <Check size={ICON_SIZE} aria-hidden="true" />
          Saved
        </span>
      )}
    </span>
  );

  return (
    <section
      className={cn("@container w-full", padding)}
      aria-label="Store selection"
    >
      {/* Onboarding-style layout: search + lists in the left column, map filling the right */}
      <div
        className={cn("grid grid-cols-1 gap-4", showMap && "@3xl:grid-cols-2")}
      >
        <div className="flex min-w-0 flex-col gap-4">
          {children}
          <StoreSelectorGrid compact={compact} saveIndicator={saveIndicator} />

          {fieldState.error ? (
            <div role="alert" className="alert alert-error alert-soft mx-2">
              <span>{fieldState.error.message}</span>
            </div>
          ) : saveError && !isPending ? (
            <div role="alert" className="alert alert-error alert-soft mx-2">
              <span>{getUserFriendlyMessage(saveError)}</span>
            </div>
          ) : null}
        </div>

        {showMap && (
          <DynamicFindStoresMap
            className="rounded-box @3xl:block hidden h-full min-h-[33dvh] w-full overflow-hidden"
            enableSearchBar={false}
            enableStoreSelection={true}
            enableTransit={false}
            zoom={10}
          />
        )}
      </div>
    </section>
  );
}
